import {
  InvalidResponseError,
  adaptAudit,
  adaptCreatedGatewayKey,
  adaptCreatedRelay,
  adaptGatewayKeys,
  adaptGatewayStatus,
  adaptMeters,
  adaptObservedUsage,
  adaptQuota,
  adaptRelayStatus,
  adaptRelays,
} from "./adapters";
import type {
  AuditEvent,
  GatewayKey,
  GatewayKeyState,
  GatewayStatus,
  KeyLimits,
  MeterRecord,
  ObservedUsage,
  ObservedUsageGroup,
  ObservedUsageWindow,
  QuotaView,
  Relay,
  RelayState,
  RelayStatusView,
} from "../model";

const maximumResponseBytes = 1024 * 1024;

export type ApiErrorKind = "http" | "network" | "invalid_response";

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly code: string;
  readonly status: number | null;
  readonly mutationOutcomeUnknown: boolean;

  constructor(options: {
    kind: ApiErrorKind;
    code: string;
    status?: number | null;
    mutationOutcomeUnknown?: boolean;
  }) {
    super(options.code);
    this.name = "ApiError";
    this.kind = options.kind;
    this.code = options.code;
    this.status = options.status ?? null;
    this.mutationOutcomeUnknown = options.mutationOutcomeUnknown ?? false;
  }
}

export interface CreateRelayInput {
  displayName: string;
  baseURL: string;
  stateFingerprint: string;
  dataPlaneCredential: string;
}

export interface CreateGatewayKeyInput {
  label: string;
  eligibleRelayIDs: string[];
  limits: KeyLimits;
}

export interface AdminApi {
  markAuthenticated(): void;
  session: {
    probe(): Promise<{ authenticated: false } | { authenticated: true; status: GatewayStatus }>;
    login(secret: string): Promise<void>;
    logout(): Promise<void>;
  };
  gateway: {
    status(): Promise<GatewayStatus>;
    activity(limit?: number): Promise<MeterRecord[]>;
    observedUsage(window?: ObservedUsageWindow, groupBy?: ObservedUsageGroup): Promise<ObservedUsage>;
    audit(limit?: number): Promise<AuditEvent[]>;
  };
  relays: {
    list(): Promise<Relay[]>;
    create(input: CreateRelayInput): Promise<Relay>;
    rename(relayID: string, displayName: string): Promise<void>;
    updateConnection(relayID: string, baseURL: string, dataPlaneCredential: string): Promise<void>;
    setState(relayID: string, state: RelayState): Promise<void>;
    quota(relayID: string): Promise<QuotaView>;
    status(relayID: string): Promise<RelayStatusView>;
  };
  keys: {
    list(): Promise<GatewayKey[]>;
    create(input: CreateGatewayKeyInput): Promise<{ key: GatewayKey; bearer: string }>;
    setState(keyID: string, state: GatewayKeyState): Promise<void>;
    setLimits(keyID: string, limits: KeyLimits): Promise<void>;
    addEligibleRelay(keyID: string, relayID: string): Promise<void>;
    removeEligibleRelay(keyID: string, relayID: string): Promise<void>;
  };
}

interface AdminApiOptions {
  fetcher?: typeof fetch;
  basePath?: string;
  onUnauthorized?: () => void;
}

type Decoder<T> = (value: unknown) => T;
type UnauthorizedPolicy = "global" | "local";

function pathID(value: string): string {
  return encodeURIComponent(value);
}

function limitValue(limit: number): number {
  return Math.max(1, Math.min(1000, Math.trunc(limit)));
}

async function readBoundedText(response: Response): Promise<string> {
  const declaredLength = Number(response.headers.get("content-length"));
  if (Number.isFinite(declaredLength) && declaredLength > maximumResponseBytes) {
    throw new InvalidResponseError();
  }

  if (response.body === null) return "";

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let body = "";
  let receivedBytes = 0;
  try {
    while (true) {
      const chunk = await reader.read();
      if (chunk.done) break;
      receivedBytes += chunk.value.byteLength;
      if (receivedBytes > maximumResponseBytes) {
        await reader.cancel();
        throw new InvalidResponseError();
      }
      body += decoder.decode(chunk.value, { stream: true });
    }
    body += decoder.decode();
    return body;
  } finally {
    reader.releaseLock();
  }
}

function decodeErrorCode(value: unknown): string {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return "unexpected_error";
  const error = (value as Record<string, unknown>).error;
  if (error === null || typeof error !== "object" || Array.isArray(error)) return "unexpected_error";
  const code = (error as Record<string, unknown>).code;
  return typeof code === "string" && code.length > 0 ? code : "unexpected_error";
}

function decodeAuthenticated(value: unknown): void {
  if (
    value === null ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    (value as Record<string, unknown>).authenticated !== true
  ) {
    throw new InvalidResponseError();
  }
}

export function createAdminApi(options: AdminApiOptions = {}): AdminApi {
  const fetcher = options.fetcher ?? fetch.bind(globalThis);
  const basePath = (options.basePath ?? "/admin/v1").replace(/\/$/, "");
  const onUnauthorized = options.onUnauthorized;
  let unauthorizedNotified = false;

  function markAuthenticated(): void {
    unauthorizedNotified = false;
  }

  async function request<T>(
    path: string,
    init: RequestInit,
    decoder: Decoder<T> | null,
    requestOptions: { mutation?: boolean; unauthorized?: UnauthorizedPolicy } = {},
  ): Promise<T> {
    const mutation = requestOptions.mutation ?? false;
    let response: Response;
    try {
      response = await fetcher(`${basePath}${path}`, {
        ...init,
        credentials: "include",
        headers: {
          Accept: "application/json",
          ...init.headers,
        },
      });
    } catch {
      throw new ApiError({
        kind: "network",
        code: "network_error",
        mutationOutcomeUnknown: mutation,
      });
    }

    if (!response.ok) {
      let code = "unexpected_error";
      try {
        const body = await readBoundedText(response);
        if (body !== "") code = decodeErrorCode(JSON.parse(body) as unknown);
      } catch {
        code = "unexpected_error";
      }
      if (response.status === 401 && requestOptions.unauthorized !== "local" && !unauthorizedNotified) {
        unauthorizedNotified = true;
        onUnauthorized?.();
      }
      throw new ApiError({ kind: "http", code, status: response.status });
    }

    if (response.status === 204) {
      if (decoder === null) return undefined as T;
      throw new ApiError({
        kind: "invalid_response",
        code: "invalid_response",
        status: response.status,
        mutationOutcomeUnknown: mutation,
      });
    }
    if (decoder === null) return undefined as T;
    try {
      const body = await readBoundedText(response);
      if (body === "") throw new InvalidResponseError();
      return decoder(JSON.parse(body) as unknown);
    } catch {
      throw new ApiError({
        kind: "invalid_response",
        code: "invalid_response",
        status: response.status,
        mutationOutcomeUnknown: mutation,
      });
    }
  }

  function json(method: string, body: unknown): RequestInit {
    return {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    };
  }

  const api: AdminApi = {
    markAuthenticated,
    session: {
      async probe() {
        try {
          const status = await request("/status", { method: "GET" }, adaptGatewayStatus, {
            unauthorized: "local",
          });
          markAuthenticated();
          return { authenticated: true, status };
        } catch (error) {
          if (error instanceof ApiError && error.status === 401) return { authenticated: false };
          throw error;
        }
      },
      async login(secret) {
        await request("/session", json("POST", { secret }), decodeAuthenticated, {
          mutation: true,
          unauthorized: "local",
        });
        markAuthenticated();
      },
      logout() {
        return request("/session", { method: "DELETE" }, null, { mutation: true });
      },
    },
    gateway: {
      status: () => request("/status", { method: "GET" }, adaptGatewayStatus),
      activity: (limit = 100) =>
        request(`/meters?limit=${limitValue(limit)}`, { method: "GET" }, adaptMeters),
      observedUsage: (window = "24h", groupBy = "all") =>
        request(
          `/observed-usage?window=${window}&group_by=${groupBy}`,
          { method: "GET" },
          adaptObservedUsage,
        ),
      audit: (limit = 100) =>
        request(`/audit?limit=${limitValue(limit)}`, { method: "GET" }, adaptAudit),
    },
    relays: {
      list: () => request("/relays", { method: "GET" }, adaptRelays),
      create: (input) =>
        request(
          "/relays",
          json("POST", {
            display_name: input.displayName,
            base_url: input.baseURL,
            state_fingerprint: input.stateFingerprint,
            data_plane_credential: input.dataPlaneCredential,
          }),
          adaptCreatedRelay,
          { mutation: true },
        ),
      rename: (relayID, displayName) =>
        request(
          `/relays/${pathID(relayID)}/display-name`,
          json("PATCH", { display_name: displayName }),
          null,
          { mutation: true },
        ),
      updateConnection: (relayID, baseURL, dataPlaneCredential) =>
        request(
          `/relays/${pathID(relayID)}/connection`,
          json("PATCH", { base_url: baseURL, data_plane_credential: dataPlaneCredential }),
          null,
          { mutation: true },
        ),
      setState: (relayID, state) =>
        request(`/relays/${pathID(relayID)}/state`, json("PATCH", { state }), null, {
          mutation: true,
        }),
      quota: (relayID) =>
        request(`/relays/${pathID(relayID)}/quota`, { method: "GET" }, adaptQuota),
      status: (relayID) =>
        request(`/relays/${pathID(relayID)}/status`, { method: "GET" }, adaptRelayStatus),
    },
    keys: {
      list: () => request("/keys", { method: "GET" }, adaptGatewayKeys),
      create: (input) =>
        request(
          "/keys",
          json("POST", {
            label: input.label,
            eligible_relay_ids: input.eligibleRelayIDs,
            limits: input.limits,
          }),
          adaptCreatedGatewayKey,
          { mutation: true },
        ),
      setState: (keyID, state) =>
        request(`/keys/${pathID(keyID)}/state`, json("PATCH", { state }), null, {
          mutation: true,
        }),
      setLimits: (keyID, limits) =>
        request(`/keys/${pathID(keyID)}/limits`, json("PATCH", limits), null, {
          mutation: true,
        }),
      addEligibleRelay: (keyID, relayID) =>
        request(
          `/keys/${pathID(keyID)}/eligible-relays/${pathID(relayID)}`,
          { method: "POST" },
          null,
          { mutation: true },
        ),
      removeEligibleRelay: (keyID, relayID) =>
        request(
          `/keys/${pathID(keyID)}/eligible-relays/${pathID(relayID)}`,
          { method: "DELETE" },
          null,
          { mutation: true },
        ),
    },
  };

  return api;
}
